// @file Elvis client
// This package only provides functions to generate the correct URLs to call Elvis server.
// See services/elvis for the server implementation.
// Copied from `url_maker` and `cmd_manager`

const ELVIS_HOSTNAME =
  (typeof document !== 'undefined' && document.documentElement.dataset.elvisHost) || 'elvis.padletcdn.com'

enum CORS {
  NO_CORS = 'n',
  ALL = 'a',
  ORIGIN = 'o',
  PASS = 'p',
}

enum HEADER_TO_CACHE {
  AUTHORIZATION = 'a',
}

enum FRAME {
  ALLOW = 'a',
  DENY = 'd',
  ORIGIN = 'o',
  PASS = 'p',
}

interface ProxyCmds {
  expiryInDays?: number
  unique?: string
  download?: boolean
  timeoutInSeconds?: number
  cors?: CORS
  log?: boolean
  frame?: FRAME
  headersToCache?: HEADER_TO_CACHE[]
}

function parseExpiry(expiry: number): string {
  if (expiry === Infinity) return 'in'
  if (expiry < -365 || expiry > 365) {
    throw new Error('Expiry must be between -365 and 365 days')
  }
  if (expiry < 0) return `i${-expiry}`
  return expiry.toString()
}

function stringifyCmds(cmds: ProxyCmds): string {
  const cmdStr: string[] = []
  if (cmds.expiryInDays) {
    const parsedExpiry = parseExpiry(cmds.expiryInDays)
    cmdStr.push(`e_${parsedExpiry}`)
  }
  if (cmds.unique) cmdStr.push(`u_${cmds.unique}`)
  if (cmds.timeoutInSeconds) cmdStr.push(`t_${cmds.timeoutInSeconds}`)
  if (cmds.cors) cmdStr.push(`c_${cmds.cors}`)
  if (cmds.download) cmdStr.push('d_1')
  if (cmds.headersToCache) cmdStr.push(`h_${cmds.headersToCache.join('')}`)
  if (cmds.log) cmdStr.push('l_1')
  if (cmds.frame) cmdStr.push(`f_${cmds.frame}`)
  return cmdStr.sort().join('-')
}

function makeFetchProxyPath(originalUrl: string, cmds: ProxyCmds): string {
  const urlWithoutScheme = originalUrl.replace(/^https?:\/\//, '')
  const cmdStr = stringifyCmds(cmds)
  return `/1/fetch/${cmdStr}/${urlWithoutScheme}`
}

/**
 * Make a fetch proxy URL from the original URL and the proxy commands.
 * @param originalUrl The original URL to fetch.
 * @param cmds The proxy commands.
 * @returns The fetch proxy URL.
 * @example
 * makeFetchProxyUrl('https://example.com', { expiry: '1d', unique: '1' })
 * 'https://elvis.padletcdn.com/1/fetch/e_in-u_1/example.com'
 */
function makeElvisProxyUrl(originalUrl: string, cmds: ProxyCmds): string {
  return `https://${ELVIS_HOSTNAME}${makeFetchProxyPath(originalUrl, cmds)}`
}

export { CORS, ELVIS_HOSTNAME, FRAME, HEADER_TO_CACHE, makeElvisProxyUrl }

// TODO:
// - Add support to change the hostname to the internal Elvis hostname on GCS.
//   This is useful when calling Elvis from other services within the same
//   Google Cloud network.
